/**
 * WAVE — 10 wave / màn. MỖI WAVE CÓ BOSS.
 * Quái mạnh dần theo wave; boss = archetype × affix × hệ số wave.
 */
import { BOSS_AFFIXES, BOSS_ARCHETYPES, STAGE_BOSS } from './enemies';

export interface SpawnGroup {
  type: string;
  count: number;
  interval: number; // giây giữa 2 con
  delay: number; // trễ trước khi bắt đầu nhóm
}

export interface BossSpec {
  archetype: string;
  affix: string;
  name: string;
  hpMult: number;
  atkMult: number;
  defMult: number;
  special: string;
  /** boss độc quyền của màn (wave 10) */
  unique: boolean;
}

export interface WaveDef {
  wave: number;
  groups: SpawnGroup[];
  boss: BossSpec;
  hpScale: number;
  atkScale: number;
  rewardGold: number;
  /** gợi ý cho người chơi */
  tip: string;
}

/**
 * NHỊP WAVE — 3 con số chỉnh độ DÀI và độ CĂNG của mỗi wave.
 *
 * Người dùng: "Nâng thời gian các waves lên gấp đôi, hiện tại thời gian mỗi wave ngắn quá".
 *
 * ⚠️ BÀI HỌC ĐO ĐƯỢC: lần đầu tôi chỉ nhân đôi SỐ QUÂN rồi đo bằng `npm run balance` —
 * tỉ lệ qua màn sụp (wave 9 chỉ còn 38/40 trận tới được, wave 10 còn 16/40; máu tướng
 * wave 1 tụt từ 98,6% xuống 80%). Nhân đôi số quân là nhân đôi ÁP LỰC SÁT THƯƠNG, không
 * phải kéo dài thời gian.
 *
 * Cách đúng — kéo dài bằng 3 núm riêng:
 *   · `interval`: giãn nhịp spawn ⇒ wave dài hơn, sân vẫn đông vừa phải;
 *   · `hp`      : quái trâu hơn ⇒ tốn thời gian giết hơn mà KHÔNG thêm đòn đánh vào tướng;
 *   · `count`   : nhích nhẹ cho sân đỡ vắng.
 * Đổi ở ĐÂY, không rải số khắp bảng. Mỗi lần đổi phải chạy lại `npm run balance`.
 *
 * SỐ CHỐT (đo bằng `npm run balance`, 40 trận, màn 1.1 độ khó normal):
 *   · gốc                    176,8s · qua màn 95%
 *   · count 1,6 · interval 3,4 · hp 1,05  ⇒  300,9s (1,70×) · qua màn 100%
 * Nghịch lý đã kiểm: tăng `count` là tăng ÁP LỰC (count 1,5 × interval 2,0 ⇒ chỉ 22,5%
 * qua màn), còn tăng `interval` là kéo dài mà GIẢM áp lực (địch vào thưa hơn). Vì vậy
 * công thức đúng là "giãn nhịp nhiều, thêm quân vừa". Wave 1 giữ nguyên (bài hướng dẫn),
 * wave 9–10 chỉ giãn nhịp để không dồn quân vào trùm.
 */
export const WAVE_PACE = { count: 1.6, interval: 3.4, hp: 1.05 };

/** Cấu trúc quân của từng wave (chỉ số quái gốc, chưa nhân scale). */
const WAVE_PLAN: Array<{ groups: Array<[string, number, number, number]>; tip: string }> = [
  { groups: [['thug', 4, 0.9, 0]], tip: 'Khởi động: một mình cân cả xóm, không mất giọt máu nào.' },
  { groups: [['thug', 8, 0.7, 0], ['runner', 3, 1.1, 3]], tip: 'Bắt đầu có đứa chạy nhanh.' },
  { groups: [['runner', 8, 0.55, 0], ['thug', 5, 0.9, 2]], tip: 'Thân pháp quái né rất cao — cần sát thương diện rộng.' },
  { groups: [['brawler', 5, 0.9, 0], ['thug', 5, 0.9, 2]], tip: 'Đầu gấu chợ ném đồ từ xa.' },
  { groups: [['gunner', 5, 1.0, 0], ['runner', 4, 0.8, 3]], tip: 'Lính súng bắn đau — đừng để đứng ngoài tầm.' },
  { groups: [['spawner', 2, 3.0, 0], ['thug', 8, 0.6, 1]], tip: 'Mụ buôn nước bọt đẻ quái — diệt mụ trước.' },
  { groups: [['gunner', 5, 0.8, 0], ['brawler', 5, 0.8, 2], ['thug', 8, 0.6, 1]], tip: 'Pha trộn ba phe.' },
  { groups: [['runner', 9, 0.5, 0], ['gunner', 6, 0.8, 2], ['spawner', 2, 2.5, 5]], tip: 'Áp lực cả tốc độ lẫn tầm xa.' },
  { groups: [['brawler', 8, 0.6, 0], ['gunner', 8, 0.7, 2], ['thug', 10, 0.5, 4]], tip: 'Elite wave — giữ Nhà bằng mọi giá.' },
  { groups: [['runner', 8, 0.45, 0], ['gunner', 6, 0.7, 2], ['spawner', 3, 2.2, 4], ['brawler', 6, 0.6, 6]], tip: 'TỔNG LỰC: trùm màn xuất hiện.' },
];

export function buildWaves(stageIndex = 0): WaveDef[] {
  return WAVE_PLAN.map((plan, i) => {
    const wave = i + 1;
    const isFinal = wave === 10;
    /*
     * WAVE 1 LÀ BÀI HƯỚNG DẪN — KHÔNG nhân nhịp.
     * Lời hứa thiết kế (và test `sim.test.ts`): "wave 1 tướng tank chấp cả bầy mà không
     * mất máu, Nhà nguyên vẹn". Đo sau khi nhân đôi nhịp: máu tướng wave 1 tụt 98,6% → 85,6%.
     * Giữ wave 1 nguyên bản để người mới không bị dội ngay từ trận đầu; nhịp mới áp từ wave 2.
     */
    const pace =
      wave === 1
        ? /*
           * BÀI HƯỚNG DẪN — giãn nhịp nhưng KHÔNG thêm quân, KHÔNG tăng máu.
           * Đo được: để nguyên bản thì wave 1 chỉ dài 6,1s trong khi các wave khác ~30s —
           * đúng cái người chơi phàn nàn ("thời gian mỗi wave ngắn quá") và wave đầu lại là
           * ấn tượng đầu tiên. Giãn nhịp KHÔNG làm khó hơn (địch vào thưa hơn), nên lời hứa
           * "tank chấp cả bầy không mất máu" vẫn giữ — có test `sim.test.ts` khoá lại.
           */
          { count: 1, interval: 1.6, hp: 1 }
        : wave >= 9
          ? { count: 1, interval: 1.25, hp: 1 } // wave cuối: chỉ giãn nhịp, KHÔNG thêm quân
          : WAVE_PACE;
    const archetype = BOSS_ARCHETYPES[(wave - 1) % BOSS_ARCHETYPES.length];
    const affixDef = BOSS_AFFIXES[(wave - 1) % BOSS_AFFIXES.length];

    const boss: BossSpec = isFinal
      ? {
          archetype: STAGE_BOSS.id,
          affix: 'armored',
          name: STAGE_BOSS.name,
          hpMult: 1,
          atkMult: 1,
          defMult: 1,
          special: 'summon',
          unique: true,
        }
      : {
          archetype,
          affix: affixDef.id,
          name: '',
          // boss wave 1 nhẹ, lớn dần tới wave 9
          hpMult: affixDef.hpMult * (0.55 + 0.06 * wave),
          atkMult: affixDef.atkMult * (0.6 + 0.05 * wave),
          defMult: affixDef.defMult,
          special: affixDef.special ?? 'none',
          unique: false,
        };

    return {
      wave,
      // NHỊP WAVE: nhích số quân + giãn nhịp spawn (xem chú thích ở WAVE_PACE)
      groups: plan.groups.map(([type, count, interval, delay]) => ({
        type,
        count: Math.max(1, Math.round(count * pace.count)),
        interval: interval * pace.interval,
        delay: delay * pace.interval,
      })),
      boss,
      // màn sau khó hơn: quái trâu hơn và đau hơn
      // quái trâu hơn ⇒ wave dài hơn mà không tăng áp lực sát thương lên tướng
      hpScale: (1 + 0.11 * (wave - 1)) * (1 + 0.3 * stageIndex) * pace.hp,
      atkScale: (1 + 0.065 * (wave - 1)) * (1 + 0.14 * stageIndex),
      rewardGold: 24 * wave,
      tip: plan.tip,
    };
  });
}

export const WAVES_PER_STAGE = 10;
